import { nameToTag } from './formatters';

/**
 * Generate NPC configuration for script-based scene tracking
 * @param {Array} npcNames - Array of NPC names
 * @param {Object} dialogues - Dialogue data for all NPCs
 * @param {Object} entityIds - Entity ID mappings (for custom entities)
 * @param {Object} config - Configuration object
 * @returns {string} JavaScript file content with NPC configuration
 */
export const generateScriptConfig = (npcNames, dialogues, entityIds, config) => {
  const isCustomEntity = config.npcType === 'custom';
  
  // Helper function to convert NPC name to tag/selector
  const getEntitySelector = (npcName) => {
    if (isCustomEntity && entityIds[npcName]) {
      return `@e[type=${entityIds[npcName]}]`;
    } else {
      // Use tag system (default or not_sure)
      const npcTag = nameToTag(npcName);
      return `@e[tag=${npcTag}]`;
    }
  };

  // Helper function to get entity identifier for script events
  const getEntityId = (npcName) => {
    if (isCustomEntity && entityIds[npcName]) {
      return entityIds[npcName];
    } else {
    return nameToTag(npcName);    }
  };

  // Helper function to get scene tag (consistent naming)
  const getSceneTag = (npcName) => {
    // Always use the processed NPC name for scene tags, regardless of entity type
    return nameToTag(npcName);
  };

  // Generate NPC configuration array
  const npcConfigs = npcNames.map(npcName => {
    const npcDialogues = dialogues[npcName] || [];
    const entityId = getEntityId(npcName);
    const entitySelector = getEntitySelector(npcName);
    const sceneTag = getSceneTag(npcName);
    
    // Calculate the number of scenes
    const sceneCount = npcDialogues.filter(dialogue => dialogue).length;

    return {
      entityId: entityId,
      entitySelector: entitySelector,
      sceneTag: sceneTag,
      npcName: npcName,
      defaultScene: 0,  // Just use 0 as the default scene number
      sceneCount: sceneCount > 0 ? sceneCount : 1  // Ensure at least 1 scene
    };
  });

// Generate the JavaScript configuration file with updated template literals
  const configContent = `/**
 * NPC Dialogue Configuration
 * Generated by NPC Dialogue Designer
 * Configuration: \${config.npcType === 'custom' ? 'Custom Entities' : 'Default NPCs'}, Script-based tracking
 */

import { world, system } from '@minecraft/server';

/**
 * NPC dialogue configuration for script-based scene tracking
 */
export const NPC_DIALOGUE_CONFIG = ${JSON.stringify(npcConfigs, null, 2)};

/**
 * Scene change event identifier
 */
const SCENE_CHANGE_EVENT = 'npc:change_scene';

/**
 * Helper function to open a dialogue scene for a player
 * @param {string} entitySelector - The entity selector (e.g., @e[tag=merchant] or @e[type=custom:npc])
 * @param {string} sceneName - The scene identifier
 * @param {string} playerName - The player's name
 */
function openDialogue(entitySelector, sceneName, playerName) {
    try {
        // Use world.getDimension('overworld').runCommand to execute the dialogue command
        world.getDimension('overworld').runCommand(\`dialogue open \${entitySelector} \${playerName} \${sceneName}\`);
    } catch (openError) {
        console.error(\`Failed to open dialogue scene \${sceneName}: \${openError.message}\`);
    }
}

/**
 * Change dialogue scene for a specific NPC
 * @param {string} entityId - The entity identifier (for finding the NPC config)
 * @param {number|string} sceneNumber - The scene number
 * @param {string} playerName - The player's name
 */
function changeDialogueScene(entityId, sceneNumber, playerName) {
    // Find the NPC configuration by entityId
    const npcConfig = NPC_DIALOGUE_CONFIG.find(config => config.entityId === entityId);
    
    if (!npcConfig) {
        return;
    }
    
    // Parse scene number if it's a string
    const sceneNum = typeof sceneNumber === 'string' ? parseInt(sceneNumber, 10) : sceneNumber;
    
    // Validate scene number is valid
    if (isNaN(sceneNum) || sceneNum < 0 || sceneNum >= npcConfig.sceneCount) {
        return;
    }
    
    // Save the scene state to player's dynamic property
    try {
        const player = world.getPlayers().find(p => p.name === playerName);
        if (player) {
            // Use setDynamicProperty directly on the player object
            player.setDynamicProperty(\`dialogue_state_\${entityId}\`, sceneNum);
        }
    } catch (saveError) {
        console.error(\`Failed to save dialogue state: \${saveError.message}\`);
    }
    
    // Generate the full scene name
    const sceneName = \`\${npcConfig.sceneTag}.\${sceneNum}\`;
    
    // First use dialogue change command
    try {
        world.getDimension('overworld').runCommand(\`dialogue change \${npcConfig.entitySelector} \${sceneName} \${playerName}\`);
    } catch (changeError) {
        console.error(\`Failed to change dialogue scene: \${changeError.message}\`);
    }
    
    // Then call openDialogue to ensure dialogue actually opens for the player
    openDialogue(npcConfig.entitySelector, sceneName, playerName);
}

/**
 * Initialize script event listener for scene changes
 * Call this function in your main script initialization
 */
export function initializeDialogueSystem() {
    
    // Set up player join handler to initialize dialogue states for each player
    world.afterEvents.playerSpawn.subscribe((eventData) => {
        const joinPlayer = eventData.player;
        if (!joinPlayer) return;
            
        // For each NPC, check if the player has a stored dialogue state
        // If not, set the default scene
        NPC_DIALOGUE_CONFIG.forEach((npcConfig) => {
            try {
                // Use getDynamicProperty directly on the player object
                const currentScene = joinPlayer.getDynamicProperty(\`dialogue_state_\${npcConfig.entityId}\`);
                
                if (currentScene === undefined) {
                    // If no scene is set, use the default scene (usually 0)
                    const defaultScene = npcConfig.defaultScene || 0;
                    
                    // Set the default scene using direct property access
                    joinPlayer.setDynamicProperty(\`dialogue_state_\${npcConfig.entityId}\`, defaultScene);
                    
                    // Generate the full scene name
                    const sceneName = \`\${npcConfig.sceneTag}.\${defaultScene}\`;
                    
                    // Use dialogue change before opening the dialogue
                    try {
                        world.getDimension('overworld').runCommand(\`dialogue change \${npcConfig.entitySelector} \${sceneName} \${joinPlayer.name}\`);
                    } catch (initChangeError) {
                        console.error(\`Error changing dialogue during initialization: \${initChangeError.message}\`);
                    }
                } else {
                    // For existing state, ensure the dialogue scene is set correctly
                    const sceneName = \`\${npcConfig.sceneTag}.\${currentScene}\`;
                    try {
                        world.getDimension('overworld').runCommand(\`dialogue change \${npcConfig.entitySelector} \${sceneName} \${joinPlayer.name}\`);
                    } catch (restoreError) {
                        console.error(\`Error restoring existing dialogue: \${restoreError.message}\`);
                    }
                }
            } catch (initError) {
                console.error(\`Error initializing dialogue state for NPC \${npcConfig.npcName}: \${initError.message}\`);
            }
        });
    });
    
    // Register script event handler for scene changes
    system.afterEvents.scriptEventReceive.subscribe((scriptEvent) => {
        // Only process our specific scene change event
        if (scriptEvent.id === SCENE_CHANGE_EVENT) {
            try {
                // Parse the message data
                const [eventEntityId, eventScene] = scriptEvent.message.split('|');
                
                if (!eventEntityId || eventScene === undefined) {
                    return;
                }
                
                // Extract the scene number from the scene name (e.g., "henry.1" -> "1")
                const sceneNumber = eventScene.split('.').pop();
                
                // Get player name from the initiator
                const playerName = scriptEvent.initiator?.nameTag;
                
                if (!playerName) {
                    return;
                }
                
                // Change the dialogue scene - this will also call openDialogue
                changeDialogueScene(eventEntityId, sceneNumber, playerName);
            } catch (eventError) {
                console.error(\`Error processing scene change event: \${eventError.message}\`);
            }
        }
    }, { namespaces: ['npc'] });
}

/**
 * Get the next scene number for an NPC dialogue
 * @param {string} entityId - The entity identifier (for finding the NPC config)
 * @param {string} playerName - The player's name
 * @returns {number|null} The next scene number, or null if already at the last scene
 */
export function getNextScene(entityId, playerName) {
    try {
        // Find the player
        const player = world.getPlayers().find(p => p.name === playerName);
        if (!player) return null;
        
        // Find the NPC configuration
        const npcConfig = NPC_DIALOGUE_CONFIG.find(config => config.entityId === entityId);
        if (!npcConfig) return null;
        
        // Get the current scene from player's dynamic property
        const currentScene = player.getDynamicProperty(\`dialogue_state_\${entityId}\`);
        let sceneNum = 0;
        
        // Parse the current scene
        if (currentScene !== undefined) {
            if (typeof currentScene === 'string' && currentScene.includes('.')) {
                const parts = currentScene.split('.');
                sceneNum = parseInt(parts[1], 10);
            } else {
                sceneNum = parseInt(currentScene, 10);
            }
        }
        
        // Check if we're already at the last scene
        if (isNaN(sceneNum) || sceneNum >= npcConfig.sceneCount - 1) {
            return null; // Already at last scene
        }
        
        return sceneNum + 1; // Return the next scene number
    } catch (nextError) {
        console.error(\`Error getting next scene: \${nextError.message}\`);
        return null;
    }
}

/**
 * Get the previous scene number for an NPC dialogue
 * @param {string} entityId - The entity identifier (for finding the NPC config)
 * @param {string} playerName - The player's name
 * @returns {number|null} The previous scene number, or null if already at the first scene
 */
export function getPreviousScene(entityId, playerName) {
    try {
        // Find the player
        const player = world.getPlayers().find(p => p.name === playerName);
        if (!player) return null;
        
        // Get the current scene from player's dynamic property
        const currentScene = player.getDynamicProperty(\`dialogue_state_\${entityId}\`);
        let sceneNum = 0;
        
        // Parse the current scene
        if (currentScene !== undefined) {
            if (typeof currentScene === 'string' && currentScene.includes('.')) {
                const parts = currentScene.split('.');
                sceneNum = parseInt(parts[1], 10);
            } else {
                sceneNum = parseInt(currentScene, 10);
            }
        }
        
        if (isNaN(sceneNum) || sceneNum <= 0) {
            return null; // Invalid scene number or already at first scene
        }
        
        return sceneNum - 1; // Return the previous scene number
    } catch (prevError) {
        console.error(\`Error getting previous scene: \${prevError.message}\`);
        return null;
    }
}

// Export the configuration for external use
export default {
    NPC_DIALOGUE_CONFIG,
    initializeDialogueSystem,
    getNextScene,
    getPreviousScene
};`;

  return configContent;
};

/**
 * Generate updated commands for script-based tracking
 * @param {Object} commands - Original command data
 * @param {Object} entityIds - Entity ID mappings (for custom entities)
 * @param {Object} config - Configuration object
 * @returns {Object} Updated commands with script events instead of scoreboard commands
 */
export const generateScriptCommands = (commands, entityIds, config) => {
  const isCustomEntity = config.npcType === 'custom';
  
  // Helper function to get entity identifier for script events
  const getEntityId = (npcName) => {
    if (isCustomEntity && entityIds[npcName]) {
      return entityIds[npcName];
    } else {
      return nameToTag(npcName);
    }
  };

  // Helper function to get scene tag (consistent naming)
  const getSceneTag = (npcName) => {
    // Always use the processed NPC name for scene tags, regardless of entity type
    return nameToTag(npcName);
  };

  const updatedCommands = {};

  // Process all commands and replace scoreboard commands with script events
  Object.entries(commands).forEach(([npc, dialogueCommands]) => {
    const entityId = getEntityId(npc);
    const sceneTag = getSceneTag(npc); // Use consistent scene tag
    updatedCommands[npc] = {};
    
    Object.entries(dialogueCommands).forEach(([dialogue, buttonCommands]) => {
      updatedCommands[npc][dialogue] = {};
      
      Object.entries(buttonCommands).forEach(([button, cmdList]) => {
        const updatedCmdList = cmdList.map(cmd => {
          // Replace dialogue open commands with script-event based commands
          if (cmd.includes('/dialogue open')) {
            const match = cmd.match(/\/dialogue open @e\[tag=([^\]]+)\][^.]+\.(\d+)/);
            if (match && match.length >= 3) {
              const targetSceneNum = match[2];
              return `/scriptevent npc:change_scene ${entityId}|${targetSceneNum}`;
            }
          }
          return cmd;
        });
        
        updatedCommands[npc][dialogue][button] = updatedCmdList;
      });
    });
  });

  return updatedCommands;
};

/**
 * Generate a JSON file for a single NPC for script-based tracking
 * @param {string} npc - NPC name
 * @param {Array} dialogues - Array of dialogues for this NPC
 * @param {Object} buttons - Button data for this NPC
 * @param {Object} commands - Command data for this NPC (already updated for scripts)
 * @param {Object} entityIds - Entity ID mappings
 * @param {Object} config - Configuration object
 * @returns {Object} JSON object representing the NPC's dialogues
 */
export const generateNpcJsonForScripts = (npc, dialogues, buttons, commands, entityIds, config) => {
  const sceneTag = nameToTag(npc); // Use scene tag consistently
  
  const npcJson = {
    "format_version": "1.17.100",
    "minecraft:npc_dialogue": {
      "scenes": []
    }
  };

  dialogues.forEach((dialogue, index) => {
    if (!dialogue) return;
    
    const buttonArray = buttons && buttons[dialogue] ? buttons[dialogue].filter(button => button !== '$placeholder_button') : [];
    
    const buttonObjects = buttonArray.map(button => {
      const buttonLowercase = nameToTag(button);
      return {
        "name": {
          "rawtext": [
            {
              "translate": `dialogue.button.${buttonLowercase}`
            }
          ]
        },
        "commands": commands && commands[dialogue] && commands[dialogue][button] ? 
          // Update command format to use only numbers for scene references
          commands[dialogue][button].map(cmd => {
            // If it's a script event, update to use just the number
            if (cmd.includes('npc:change_scene')) {
              const parts = cmd.split('|');
              if (parts.length === 2) {
                const entityId = parts[0].replace('/scriptevent npc:change_scene ', '');
                const fullScene = parts[1];
                // Extract just the number if it contains a period
                let sceneNum = fullScene;
                if (fullScene.includes('.')) {
                  sceneNum = fullScene.split('.')[1];
                }
                return `/scriptevent npc:change_scene ${entityId}|${sceneNum}`;
              }
            }
            return cmd;
          })
          : []
      };
    });

    npcJson["minecraft:npc_dialogue"]["scenes"].push({
      "scene_tag": `${sceneTag}.${index}`, // Use scene tag consistently
      "npc_name": {
        "rawtext": [
          {
            "translate": `dialogue.${sceneTag}.name` // Use scene tag consistently
          }
        ]
      },
      "text": {
        "rawtext": [
          {
            "translate": `dialogue.${sceneTag}.${index}`, // Use scene tag consistently
            "with": ["\n"]
          }
        ]
      },
      "buttons": buttonObjects,
      "on_close_commands": []
    });
  });

  return npcJson;
};

/**
 * Helper function to generate lang file content (reused from original exporters)
 */
export const generateLangFileContent = (npcNames, dialogues, buttons) => {
  let langData = '';
  let buttonData = '';
  const processedButtons = new Set();

  for (const npc of npcNames) {
    if (!dialogues[npc] || dialogues[npc].length === 0) continue;
    
    const sceneTag = nameToTag(npc); // Use scene tag consistently
    langData += `dialogue.${sceneTag}.name=${npc}\n`;

    const npcDialogues = dialogues[npc];
    for (let i = 0; i < npcDialogues.length; i++) {
      const dialogue = npcDialogues[i];
      if (!dialogue) continue;
      
      langData += `dialogue.${sceneTag}.${i}=${dialogue}\n`; // Use scene tag consistently
      
      // Process buttons for this dialogue
      if (!buttons[npc] || !buttons[npc][dialogue]) continue;
      
      const filteredButtons = buttons[npc][dialogue].filter(button => button !== '$placeholder_button');
      for (let j = 0; j < filteredButtons.length; j++) {
        const button = filteredButtons[j];
        const buttonLowercase = nameToTag(button);
        const buttonKey = `dialogue.button.${buttonLowercase}`;
        
        if (!processedButtons.has(buttonKey)) {
          buttonData += `${buttonKey}=${button}\n`;
          processedButtons.add(buttonKey);
        }
      }
    }
  }

  return langData + buttonData;
};

/**
 * Download a file with the given content
 * @param {string} filename - The name of the file to download
 * @param {string} content - The content of the file
 * @param {string} type - The MIME type of the file
 */
const downloadFile = (filename, content, type = 'text/javascript') => {
  try {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 200);
    return true;
  } catch (error) {
    console.error(`Error downloading file '${filename}':`, error);
    return false;
  }
};

/**
 * Export script-based configuration files
 * @param {Array} npcNames - Array of NPC names
 * @param {Object} dialogues - Dialogue data for all NPCs
 * @param {Object} buttons - Button data for all NPCs
 * @param {Object} commands - Command data for all NPCs
 * @param {Object} entityIds - Entity ID mappings (for custom entities)
 * @param {Object} config - Configuration object
 */
export const exportScriptFiles = async (npcNames, dialogues, buttons, commands, entityIds, config) => {
  try {
    let filesCreated = 0;
    const errors = [];

    // Generate and export the main script configuration
    const scriptConfig = generateScriptConfig(npcNames, dialogues, entityIds, config);
    if (downloadFile('npcDialogueConfig.js', scriptConfig, 'text/javascript')) {
      filesCreated++;
    } else {
      errors.push('Failed to create npcDialogueConfig.js');
    }

    // Generate updated commands for script-based tracking
    const updatedCommands = generateScriptCommands(commands, entityIds, config);

    // Export individual NPC JSON files with updated commands
    for (const npc of npcNames) {
      if (!dialogues[npc] || dialogues[npc].length === 0) continue;
      
      const sceneTag = nameToTag(npc);
      const npcJson = generateNpcJsonForScripts(npc, dialogues[npc], buttons[npc], updatedCommands[npc], entityIds, config);
      
      const jsonContent = JSON.stringify(npcJson, null, 2);
      if (downloadFile(`${sceneTag}.json`, jsonContent, 'application/json')) {
        filesCreated++;
      } else {
        errors.push(`Failed to create ${sceneTag}.json`);
      }
      
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Generate and export lang file (same as before)
    const langContent = generateLangFileContent(npcNames, dialogues, buttons);
    if (downloadFile('dialogues.lang', langContent, 'text/plain')) {
      filesCreated++;
    } else {
      errors.push('Failed to create dialogues.lang');
    }

    // Show success message
    if (errors.length === 0) {
      alert(`Script-based export completed successfully!\n${filesCreated} files were created.\n\nDon't forget to call initializeDialogueSystem() in your main script!`);
    } else {
      alert(`Script-based export completed with some issues:\n- ${errors.join('\n- ')}\n\n${filesCreated} files were created successfully.`);
    }

  } catch (error) {
    console.error("Error during script export:", error);
    alert(`Script export error: ${error.message}\n\nCheck the console for more details.`);
  }
};

/**
 * Enhanced export function that works with traditional export system too
 * @param {Array} npcNames - Array of NPC names
 * @param {Object} dialogues - Dialogue data for all NPCs
 * @param {Object} buttons - Button data for all NPCs
 * @param {Object} commands - Command data for all NPCs
 * @param {Object} entityIds - Entity ID mappings (for custom entities)
 * @param {Object} config - Configuration object
 */
export const enhancedExportAllFiles = async (npcNames, dialogues, buttons, commands, entityIds, config) => {
  
  const isCustomEntity = config.npcType === 'custom';
  const sortedNpcNames = [...npcNames];
  let filesCreated = 0;
  const errors = [];
  
  try {
    console.log("Enhanced export for NPCs:", sortedNpcNames);
    console.log("Configuration:", config);
    
    // Export individual NPC JSON files
    for (const npc of sortedNpcNames) {
      if (!dialogues[npc] || dialogues[npc].length === 0) continue;
      
      const sceneTag = nameToTag(npc); // Use scene tag consistently
      const npcJson = generateEnhancedNpcJson(npc, dialogues[npc], buttons[npc], commands[npc], entityIds, config);
      
      const jsonContent = JSON.stringify(npcJson, null, 2);
      if (downloadFile(`${sceneTag}.json`, jsonContent, 'application/json')) {
        filesCreated++;
      } else {
        errors.push(`Failed to create ${sceneTag}.json`);
      }
      
      if (sortedNpcNames.length > 5) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Generate and export lang file
    const langContent = generateLangFileContent(sortedNpcNames, dialogues, buttons);
    if (downloadFile('dialogues.lang', langContent)) {
      filesCreated++;
    } else {
      errors.push('Failed to create dialogues.lang');
    }
    
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Generate and export mcfunction file (traditional system)
    const mcfunctionContent = generateEnhancedMcFunctionContent(sortedNpcNames, dialogues, entityIds, config);
    
    if (downloadFile('dialogue_change.mcfunction', mcfunctionContent || '# No commands generated')) {
      filesCreated++;
    } else {
      errors.push('Failed to create dialogue_change.mcfunction');
    }
    
    // Show success message
    if (errors.length === 0) {
      alert(`Enhanced export completed successfully!\n${filesCreated} files were created.\n\n${isCustomEntity ? 'For custom entities, scene names use the NPC name format (e.g., craig.1) for consistency across all files.' : ''}`);
    } else {
      alert(`Enhanced export completed with some issues:\n- ${errors.join('\n- ')}\n\n${filesCreated} files were created successfully.`);
    }
    
  } catch (error) {
    console.error("Error during enhanced export:", error);
    alert(`Enhanced export error: ${error.message}\n\nCheck the console for more details.`);
  }
};

/**
 * Generate a JSON file for a single NPC (enhanced version)
 * @param {string} npc - NPC name
 * @param {Array} dialogues - Array of dialogues for this NPC
 * @param {Object} buttons - Button data for this NPC
 * @param {Object} commands - Command data for this NPC
 * @param {Object} entityIds - Entity ID mappings
 * @param {Object} config - Configuration object
 * @returns {Object} JSON object representing the NPC's dialogues
 */
const generateEnhancedNpcJson = (npc, dialogues, buttons, commands, entityIds, config) => {
  const sceneTag = nameToTag(npc); // Use scene tag consistently
  
  const npcJson = {
    "format_version": "1.17.100",
    "minecraft:npc_dialogue": {
      "scenes": []
    }
  };

  dialogues.forEach((dialogue, index) => {
    if (!dialogue) return;
    
    const buttonArray = buttons && buttons[dialogue] ? buttons[dialogue].filter(button => button !== '$placeholder_button') : [];
    
    const buttonObjects = buttonArray.map(button => {
      const buttonLowercase = nameToTag(button);
      return {
        "name": {
          "rawtext": [
            {
              "translate": `dialogue.button.${buttonLowercase}`
            }
          ]
        },
        "commands": commands && commands[dialogue] && commands[dialogue][button] ? commands[dialogue][button] : []
      };
    });

    npcJson["minecraft:npc_dialogue"]["scenes"].push({
      "scene_tag": `${sceneTag}.${index}`, // Use scene tag consistently
      "npc_name": {
        "rawtext": [
          {
            "translate": `dialogue.${sceneTag}.name` // Use scene tag consistently
          }
        ]
      },
      "text": {
        "rawtext": [
          {
            "translate": `dialogue.${sceneTag}.${index}`, // Use scene tag consistently
            "with": ["\n"]
          }
        ]
      },
      "buttons": buttonObjects,
      "on_close_commands": []
    });
  });

  return npcJson;
};

/**
 * Generate content for the .mcfunction file (enhanced version with player-specific states)
 * @param {Array} npcNames - Array of NPC names
 * @param {Object} dialogues - Dialogue data for all NPCs
 * @param {Object} entityIds - Entity ID mappings
 * @param {Object} config - Configuration object
 * @returns {string} Content for .mcfunction file
 */
const generateEnhancedMcFunctionContent = (npcNames, dialogues, entityIds, config) => {
  const isCustomEntity = config.npcType === 'custom';
  let mcfunctionData = '';

  // Add header comments for the mcfunction file
  mcfunctionData += `# NPC Dialogue Change Function with Player-Specific States\n`;
  mcfunctionData += `# Generated by NPC Dialogue Designer\n\n`;
  
  // Initialize all score objectives for each NPC at the start
  mcfunctionData += `# Initialize scoreboard objectives if they don't exist\n`;
  for (const npc of npcNames) {
    if (!dialogues[npc]) continue;
    
    // For scoreboard objective name: ALWAYS use scene tag (no special characters allowed)
    const scoreboardName = nameToTag(npc);
    
    mcfunctionData += `scoreboard objectives add ${scoreboardName} dummy\n`;
  }
  
  mcfunctionData += `\n# Apply dialogue states for connected players\n`;

  for (const npc of npcNames) {
    if (!dialogues[npc]) continue;
    
    const npcDialogues = dialogues[npc];
    
    // For entity selector: use full entity ID for custom entities, tag for default NPCs
    const entitySelector = isCustomEntity && entityIds[npc] 
      ? `@e[type=${entityIds[npc]}]` 
      : `@e[tag=${nameToTag(npc)}]`;
    
    // For scoreboard objective name: ALWAYS use scene tag (no special characters allowed)
    const scoreboardName = nameToTag(npc);
    
    // For scene name: use scene tag consistently
    const sceneTag = nameToTag(npc);
    
    let maxIndex = 0;
    
    if (Array.isArray(npcDialogues)) {
      maxIndex = Math.max(0, npcDialogues.length - 1);
    } else if (typeof npcDialogues === 'object') {
      const keys = Object.keys(npcDialogues)
        .filter(key => !isNaN(Number(key)))
        .map(Number);
      
      maxIndex = keys.length > 0 ? Math.max(...keys) : 0;
    }
    
    mcfunctionData += `\n# Dialogue states for ${npc}\n`;
    
    for (let index = 0; index <= maxIndex; index++) {
      // Use scoreboardName (scene tag) for scoreboard objective and sceneTag for scene name
      // Execute as the player and include @s in the dialogue change command
      mcfunctionData += `execute as @a[scores={${scoreboardName}=${index}}] run dialogue change ${entitySelector} ${sceneTag}.${index} @s\n`;
    }
  }

  // Add initialization logic for players who have no scoreboard value yet
  mcfunctionData += `\n# Initialize new players with default dialogue state (0)\n`;
  for (const npc of npcNames) {
    if (!dialogues[npc]) continue;
    
    // For scoreboard objective name: ALWAYS use scene tag (no special characters allowed)
    const scoreboardName = nameToTag(npc);
    
    // For entity selector: use full entity ID for custom entities, tag for default NPCs
    const entitySelector = isCustomEntity && entityIds[npc] 
      ? `@e[type=${entityIds[npc]}]` 
      : `@e[tag=${nameToTag(npc)}]`;
    
    // For scene name: use scene tag consistently
    const sceneTag = nameToTag(npc);
    
    mcfunctionData += `execute as @a unless score @s ${scoreboardName} matches 0.. run scoreboard players set @s ${scoreboardName} 0\n`;
    mcfunctionData += `execute as @a unless score @s ${scoreboardName} matches 0.. run dialogue change ${entitySelector} ${sceneTag}.0 @s\n`;
  }

  return mcfunctionData;
};
